import { Controller } from "@hotwired/stimulus"
import { Editor, Extension } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Image from "@tiptap/extension-image"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Highlight from "@tiptap/extension-highlight"
import BubbleMenu from "@tiptap/extension-bubble-menu"
import Suggestion from "@tiptap/suggestion"
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight"
import { createLowlight, common } from "lowlight"

const TITLE_MAX_LENGTH = 100

export default class extends Controller {
  static targets = ["content", "input", "form", "status", "title", "bubble", "slashMenu", "file", "insertMenu", "inlineInsert", "deleteButton", "formatButtons", "draftActions", "publishLink", "archiveLink", "deleteLink"]
  static values = { autosaveUrl: String }

  connect() {
    this.boundInsertClose = this.handleInsertOutsideClick.bind(this)
    this.boundInsertKeydown = this.handleInsertKeydown.bind(this)
    document.addEventListener("click", this.boundInsertClose)
    document.addEventListener("keydown", this.boundInsertKeydown)
    const controller = this
    const languages = [
      { value: "", label: "Plain" },
      { value: "ruby", label: "Ruby" },
      { value: "python", label: "Python" },
      { value: "javascript", label: "JavaScript" },
      { value: "typescript", label: "TypeScript" },
      { value: "c", label: "C" },
      { value: "cpp", label: "C++" },
      { value: "go", label: "Go" },
      { value: "html", label: "HTML" },
      { value: "css", label: "CSS" },
      { value: "json", label: "JSON" },
      { value: "bash", label: "Bash" }
    ]
    const SlashCommand = Extension.create({
      name: "slash-command",
      addProseMirrorPlugins() {
        return [
          Suggestion({
            editor: this.editor,
            char: "/",
            startOfLine: true,
            allow: ({ state }) => state.selection.$from.parent.type.name !== "codeBlock",
            command: ({ editor, props }) => {
              props.command(editor)
            },
            items: ({ query }) => {
              const items = controller.slashItems()
              return items.filter((item) => item.title.toLowerCase().includes(query.toLowerCase()))
            },
            render: () => controller.renderSlashMenu()
          })
        ]
      }
    })

    const lowlight = createLowlight(common)
    const CodeBlock = CodeBlockLowlight.extend({
      addKeyboardShortcuts() {
        return {
          Tab: () => {
            if (!this.editor.isActive("codeBlock")) return false
            this.editor.commands.insertContent("  ")
            return true
          }
        }
      },
      addNodeView() {
        return ({ node, editor, getPos }) => {
          const wrapper = document.createElement("div")
          wrapper.className = "code-block-wrapper"

          const header = document.createElement("div")
          header.className = "code-block-header"

          const label = document.createElement("span")
          label.className = "code-block-label"
          label.textContent = "Language"

          const select = document.createElement("select")
          select.className = "code-block-select"
          select.title = "Choose language"
          languages.forEach((lang) => {
            const option = document.createElement("option")
            option.value = lang.value
            option.textContent = lang.label
            if ((node.attrs.language || "") === lang.value) option.selected = true
            select.appendChild(option)
          })

          select.addEventListener("change", (event) => {
            const value = event.target.value || null
            editor
              .chain()
              .focus()
              .command(({ tr }) => {
                tr.setNodeMarkup(getPos(), undefined, { ...node.attrs, language: value })
                return true
              })
              .run()
          })

          const pre = document.createElement("pre")
          const code = document.createElement("code")

          header.appendChild(label)
          header.appendChild(select)
          pre.appendChild(code)
          wrapper.appendChild(header)
          wrapper.appendChild(pre)

          return {
            dom: wrapper,
            contentDOM: code,
            update: (updatedNode) => {
              if (updatedNode.type.name !== "codeBlock") return false
              select.value = updatedNode.attrs.language || ""
              return true
            }
          }
        }
      }
    })

    const initialContent = this.normalizeContent(this.parseContent())
    this.editor = new Editor({
      element: this.contentTarget,
      extensions: [
        StarterKit.configure({ codeBlock: false, link: false }),
        Highlight,
        Image,
        Link.configure({ openOnClick: false }),
        Placeholder.configure({ placeholder: "Tell your story..." }),
        CodeBlock.configure({ lowlight }),
        BubbleMenu.configure({
          element: this.bubbleTarget,
          shouldShow: ({ editor }) => {
            return !editor.state.selection.empty && !editor.isActive("codeBlock")
          }
        }),
        SlashCommand
      ],
      content: initialContent,
      editorProps: {
        attributes: {
          class: "min-h-[240px] font-serif text-lg leading-relaxed text-ink outline-none"
        },
        handleDrop: (view, event) => this.handleDrop(event),
        handlePaste: (view, event) => this.handlePaste(event),
        handleClick: (view, pos, event) => this.handleEditorClick(view, pos, event)
      },
      onUpdate: () => {
        this.syncInput()
        this.markDirty()
        this.updateInlineInsert()
        if (!this.hasAutosaveUrlValue) {
          this.createDraftIfNeeded()
        } else {
          this.queueAutosave()
        }
      },
      onSelectionUpdate: () => {
        this.updateBubble()
        this.updateInlineInsert()
      },
      onFocus: () => {
        this.updateInlineInsert()
      }
    })

    this.syncInput()
    this.startAutosave()
    this.updateInlineInsert()
    this.focusEditorAfterLoad()
  }

  focusEditorAfterLoad() {
    const run = () => {
      if (document.visibilityState !== "visible") return
      if (!this.editor) return
      this.editor.commands.focus()
      this.updateInlineInsert()
    }
    if (document.visibilityState === "visible") {
      requestAnimationFrame(() => setTimeout(run, 120))
    } else {
      const onVisible = () => {
        document.removeEventListener("visibilitychange", onVisible)
        requestAnimationFrame(() => setTimeout(run, 120))
      }
      document.addEventListener("visibilitychange", onVisible)
    }
  }

  disconnect() {
    if (this.editor) this.editor.destroy()
    if (this.autosaveTimer) clearInterval(this.autosaveTimer)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    document.removeEventListener("click", this.boundInsertClose)
    document.removeEventListener("keydown", this.boundInsertKeydown)
  }

  preventSubmit(event) {
    event.preventDefault()
  }

  showDraftActions(slug) {
    if (!this.hasDraftActionsTarget) return
    const publishUrl = `/stories/${slug}/publish`
    const archiveUrl = `/stories/${slug}/archive`
    const deleteUrl = `/stories/${slug}`
    if (this.hasPublishLinkTarget) this.publishLinkTarget.setAttribute("href", publishUrl)
    if (this.hasArchiveLinkTarget) this.archiveLinkTarget.setAttribute("href", archiveUrl)
    if (this.hasDeleteLinkTarget) this.deleteLinkTarget.setAttribute("href", deleteUrl)
    this.draftActionsTarget.classList.remove("hidden")
  }

  saveAndPublish(event) {
    if (this.openingPublishAfterSave) return
    event.preventDefault()
    if (this.hasContentTarget && this.editor) this.syncInput()
    const savePromise = this.autosaveUrlValue
      ? this.saveBeforePublish()
      : this.createDraftThenPublish()
    savePromise
      .then(() => {
        this.openingPublishAfterSave = true
        if (this.hasPublishLinkTarget) this.publishLinkTarget.click()
        setTimeout(() => { this.openingPublishAfterSave = false }, 0)
      })
      .catch(() => {
        if (this.hasStatusTarget) this.statusTarget.textContent = "Save failed. Try again."
      })
  }

  saveBeforePublish() {
    const url = this.autosaveUrlValue || this.formTarget?.action
    if (!url) return Promise.resolve()
    const title = this.hasTitleTarget ? this.titleTarget.value : ""
    if (title.length > TITLE_MAX_LENGTH) {
      if (this.hasStatusTarget) this.statusTarget.textContent = `Title is too long (maximum ${TITLE_MAX_LENGTH} characters).`
      return Promise.reject(new Error("Title too long"))
    }
    const token = document.querySelector("meta[name='csrf-token']")?.content
    if (this.hasStatusTarget) this.statusTarget.textContent = "Saving..."
    const payload = {
      story: {
        title: this.hasTitleTarget ? this.titleTarget.value : undefined,
        content: this.hasInputTarget ? this.inputTarget.value : undefined
      }
    }
    return fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": token || ""
      },
      body: JSON.stringify(payload)
    }).then(async (res) => {
      if (!res.ok) {
        await this.showValidationErrors(res)
        throw new Error("Save failed")
      }
      if (this.hasStatusTarget) this.statusTarget.textContent = "Saved"
      return res
    })
  }

  createDraftThenPublish() {
    const action = this.formTarget?.action
    if (!action) return Promise.reject(new Error("No form action"))
    const title = this.hasTitleTarget ? this.titleTarget.value.trim() : ""
    const content = this.hasInputTarget ? this.inputTarget.value : ""
    if (!title && (!content || content === "\"\"" || content === "")) {
      if (this.hasStatusTarget) this.statusTarget.textContent = "Add a title or some content first."
      return Promise.reject(new Error("Empty draft"))
    }
    if (title.length > TITLE_MAX_LENGTH) {
      if (this.hasStatusTarget) this.statusTarget.textContent = `Title is too long (maximum ${TITLE_MAX_LENGTH} characters).`
      return Promise.reject(new Error("Title too long"))
    }
    const token = document.querySelector("meta[name='csrf-token']")?.content
    if (this.hasStatusTarget) this.statusTarget.textContent = "Saving..."
    const payload = { story: { title: title || "Untitled", content } }
    return fetch(action, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": token || ""
      },
      body: JSON.stringify(payload)
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          this.showValidationErrorsFromData(data)
          throw new Error("Save failed")
        }
        return data
      })
      .then((data) => {
        if (data?.autosave_url) {
          this.autosaveUrlValue = data.autosave_url
          this.element.dataset.editorAutosaveUrlValue = data.autosave_url
        }
        if (data?.story_slug && this.hasPublishLinkTarget) {
          this.publishLinkTarget.setAttribute("href", `/stories/${data.story_slug}/publish`)
        }
        if (data?.edit_url) {
          window.history.replaceState({}, "", data.edit_url)
        }
        if (this.hasStatusTarget) this.statusTarget.textContent = "Saved"
        return data
      })
  }

  parseContent() {
    const raw = this.inputTarget.value
    if (!raw) return ""
    const trimmed = raw.trim()
    if (!trimmed) return ""
    try {
      const parsed = JSON.parse(trimmed)
      if (typeof parsed === "string") {
        const nested = parsed.trim()
        if (!nested) return ""
        return JSON.parse(nested)
      }
      return parsed
    } catch (_) {
      return ""
    }
  }

  normalizeContent(payload) {
    if (!payload || typeof payload !== "object") return payload
    if (payload.type !== "doc" || !Array.isArray(payload.content)) return payload
    if (payload.content.length < 2) return payload
    const pruned = [...payload.content]
    while (pruned.length > 1) {
      const first = pruned[0]
      const isEmptyParagraph = first?.type === "paragraph" && (!first.content || first.content.length === 0)
      const isEmptyHeading = first?.type === "heading" && (!first.content || first.content.length === 0)
      const isEmptyBlockquote =
        first?.type === "blockquote" &&
        (!first.content || (first.content.length === 1 && first.content[0]?.type === "paragraph" && (!first.content[0].content || first.content[0].content.length === 0)))
      if (isEmptyParagraph || isEmptyHeading || isEmptyBlockquote) {
        pruned.shift()
        continue
      }
      break
    }
    return { ...payload, content: pruned }
  }

  syncInput() {
    this.inputTarget.value = JSON.stringify(this.editor.getJSON())
  }

  startAutosave() {
    if (!this.hasAutosaveUrlValue) return
    this.autosaveTimer = setInterval(() => this.autosave(), 30000)
  }

  queueAutosave() {
    if (!this.hasAutosaveUrlValue) return
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.autosave(), 5000)
  }

  autosave() {
    if (!this.hasAutosaveUrlValue) return
    if (!this.isDirty) return
    if (this.isSaving) return
    const title = this.hasTitleTarget ? this.titleTarget.value : ""
    if (title.length > TITLE_MAX_LENGTH) return
    const token = document.querySelector("meta[name='csrf-token']").content
    if (this.hasStatusTarget) this.statusTarget.textContent = "Saving..."
    const payload = {
      story: {
        title: this.hasTitleTarget ? this.titleTarget.value : undefined,
        content: this.inputTarget.value
      }
    }
    const payloadKey = JSON.stringify(payload.story)
    if (this.lastSavedPayload === payloadKey) {
      this.isDirty = false
      return
    }
    this.isSaving = true

    fetch(this.autosaveUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": token
      },
      body: JSON.stringify(payload)
    }).then(async (res) => {
      if (!res.ok) {
        await this.showValidationErrors(res)
        this.isSaving = false
        return
      }
      this.lastSavedPayload = payloadKey
      this.isDirty = false
      this.isSaving = false
      if (this.hasStatusTarget) {
        this.statusTarget.textContent = `Saved ${new Date().toLocaleTimeString()}`
      }
    }).catch(() => {
      this.isSaving = false
    })
  }

  async showValidationErrors(res) {
    const data = await res.json().catch(() => ({}))
    this.showValidationErrorsFromData(data)
  }

  showValidationErrorsFromData(data) {
    if (this.hasStatusTarget && data?.errors?.length) {
      this.statusTarget.textContent = data.errors.join(". ")
    } else if (this.hasStatusTarget) {
      this.statusTarget.textContent = "Save failed. Try again."
    }
  }

  markDirty() {
    this.isDirty = true
  }

  createDraftIfNeeded() {
    if (this.draftCreating) return
    const title = this.hasTitleTarget ? this.titleTarget.value.trim() : ""
    const content = this.inputTarget.value
    if (!title && (!content || content === "\"\"")) return

    this.draftCreating = true
    const token = document.querySelector("meta[name='csrf-token']").content
    const payload = { story: { title: title || "Untitled", content } }

    fetch(this.formTarget.action, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": token
      },
      body: JSON.stringify(payload)
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.autosave_url) {
          this.autosaveUrlValue = data.autosave_url
          this.element.dataset.editorAutosaveUrlValue = data.autosave_url
        }
        if (data?.story_slug) {
          this.showDraftActions(data.story_slug)
          if (this.hasStatusTarget) this.statusTarget.textContent = "Draft saved"
        }
        if (data?.edit_url) {
          window.history.replaceState({}, "", data.edit_url)
        }
      })
      .catch(() => {
        this.draftCreating = false
      })
  }

  slashItems() {
    return [
      { title: "Heading 1", command: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run() },
      { title: "Heading 2", command: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run() },
      { title: "Heading 3", command: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run() },
      { title: "Bullet list", command: (editor) => editor.chain().focus().toggleBulletList().run() },
      { title: "Numbered list", command: (editor) => editor.chain().focus().toggleOrderedList().run() },
      { title: "Quote", command: (editor) => editor.chain().focus().toggleBlockquote().run() },
      { title: "Code block", command: (editor) => editor.chain().focus().setCodeBlock().run() },
      { title: "Divider", command: (editor) => editor.chain().focus().setHorizontalRule().run() },
      { title: "Image/GIF upload", command: () => this.fileTarget.click() },
      { title: "Image/GIF URL", command: () => this.insertImageUrl() },
      { title: "Link", command: (editor) => this.promptLink(editor) }
    ]
  }

  renderSlashMenu() {
    let selectedIndex = 0
    let items = []

    const updateMenu = () => {
      this.slashMenuTarget.innerHTML = ""
      items.forEach((item, index) => {
        const button = document.createElement("button")
        button.type = "button"
        button.className = `flex w-full items-center justify-between rounded-md px-2 py-1 text-left ${index === selectedIndex ? "bg-ink/10" : ""}`
        button.textContent = item.title
        button.addEventListener("click", () => item.command(this.editor))
        this.slashMenuTarget.appendChild(button)
      })
    }

    return {
      onStart: (props) => {
        items = props.items
        selectedIndex = 0
        this.slashOpen = true
        this.slashMenuTarget.classList.remove("hidden")
        this.slashMenuTarget.classList.remove("opacity-0", "translate-y-1", "pointer-events-none")
        this.slashMenuTarget.classList.add("opacity-100", "translate-y-0")
        const rect = props.clientRect?.()
        if (rect) {
          this.slashMenuTarget.style.left = `${rect.left}px`
          this.slashMenuTarget.style.top = `${rect.bottom + 8}px`
        }
        updateMenu()
      },
      onUpdate: (props) => {
        items = props.items
        const rect = props.clientRect?.()
        if (rect) {
          this.slashMenuTarget.style.left = `${rect.left}px`
          this.slashMenuTarget.style.top = `${rect.bottom + 8}px`
        }
        updateMenu()
      },
      onKeyDown: (props) => {
        if (props.event.key === "ArrowDown") {
          selectedIndex = (selectedIndex + 1) % items.length
          updateMenu()
          return true
        }
        if (props.event.key === "ArrowUp") {
          selectedIndex = (selectedIndex - 1 + items.length) % items.length
          updateMenu()
          return true
        }
        if (props.event.key === "Enter") {
          items[selectedIndex]?.command(this.editor)
          return true
        }
        return false
      },
      onExit: () => {
        this.slashOpen = false
        this.slashMenuTarget.classList.add("opacity-0", "translate-y-1", "pointer-events-none")
        this.slashMenuTarget.classList.remove("opacity-100", "translate-y-0")
        setTimeout(() => this.slashMenuTarget.classList.add("hidden"), 150)
        this.updateInlineInsert()
      }
    }
  }

  toggleBold() {
    this.editor.chain().focus().toggleBold().run()
  }

  toggleItalic() {
    this.editor.chain().focus().toggleItalic().run()
  }

  setHeading1() {
    this.editor.chain().focus().toggleHeading({ level: 1 }).run()
  }

  setHeading2() {
    this.editor.chain().focus().toggleHeading({ level: 2 }).run()
  }

  setHeading3() {
    this.editor.chain().focus().toggleHeading({ level: 3 }).run()
  }

  toggleBulletList() {
    this.editor.chain().focus().toggleBulletList().run()
  }

  toggleOrderedList() {
    this.editor.chain().focus().toggleOrderedList().run()
  }

  toggleBlockquote() {
    this.editor.chain().focus().toggleBlockquote().run()
  }

  toggleCodeBlock() {
    this.editor.chain().focus().toggleCodeBlock().run()
  }

  insertDivider() {
    this.editor.chain().focus().setHorizontalRule().run()
  }

  setLink() {
    this.promptLink(this.editor)
  }

  insertHeading() {
    this.editor.chain().focus().toggleHeading({ level: 2 }).run()
  }

  toggleInsertMenu() {
    const isHidden = this.insertMenuTarget.classList.contains("hidden")
    if (isHidden) {
      this.insertMenuOpen = true
      this.insertMenuTarget.classList.remove("hidden")
      this.insertMenuTarget.classList.remove("opacity-0", "translate-y-1", "pointer-events-none")
      this.insertMenuTarget.classList.add("opacity-100", "translate-y-0")
    } else {
      this.closeInsertMenu()
    }
  }

  closeInsertMenu() {
    if (!this.hasInsertMenuTarget || this.insertMenuTarget.classList.contains("hidden")) return
    this.insertMenuOpen = false
    this.insertMenuTarget.classList.add("opacity-0", "translate-y-1", "pointer-events-none")
    this.insertMenuTarget.classList.remove("opacity-100", "translate-y-0")
    setTimeout(() => this.insertMenuTarget.classList.add("hidden"), 150)
  }

  handleInsertOutsideClick(event) {
    if (!this.insertMenuOpen) return
    if (this.insertMenuTarget.contains(event.target)) return
    if (this.inlineInsertTarget.contains(event.target)) return
    this.closeInsertMenu()
  }

  handleInsertKeydown(event) {
    if (!this.insertMenuOpen) return
    if (event.key !== "Escape") return
    this.closeInsertMenu()
  }

  toggleInlineInsertMenu() {
    this.toggleInsertMenu()
    const rect = this.inlineInsertTarget.getBoundingClientRect()
    const left = rect.left + 44
    const menuRect = this.insertMenuTarget.getBoundingClientRect()
    let top = rect.bottom + 6
    if (top + menuRect.height > window.innerHeight - 8) {
      top = rect.top - menuRect.height - 6
    }
    this.insertMenuTarget.style.left = `${left}px`
    this.insertMenuTarget.style.top = `${top}px`
  }

  insertImageUpload() {
    this.insertMenuTarget.classList.add("hidden")
    this.insertMenuOpen = false
    this.fileTarget.click()
  }

  insertImageUrl() {
    this.insertMenuTarget.classList.add("hidden")
    this.insertMenuOpen = false
    const url = window.prompt("Paste image or GIF URL")
    if (!url) return
    this.editor.chain().focus().setImage({ src: url }).run()
    this.updateInlineInsert()
  }

  insertImageUrlFromMenu() {
    this.insertImageUrl()
  }

  insertCodeBlockFromMenu() {
    this.insertMenuTarget.classList.add("hidden")
    this.insertMenuOpen = false
    this.editor.chain().focus().setCodeBlock().run()
    this.updateInlineInsert()
  }

  insertQuoteFromMenu() {
    this.insertMenuTarget.classList.add("hidden")
    this.insertMenuOpen = false
    this.editor.chain().focus().toggleBlockquote().run()
    this.updateInlineInsert()
  }

  insertDividerFromMenu() {
    this.insertMenuTarget.classList.add("hidden")
    this.insertMenuOpen = false
    this.editor.chain().focus().setHorizontalRule().run()
    this.updateInlineInsert()
  }

  insertBulletListFromMenu() {
    this.insertMenuTarget.classList.add("hidden")
    this.insertMenuOpen = false
    this.editor.chain().focus().toggleBulletList().run()
    this.updateInlineInsert()
  }

  insertOrderedListFromMenu() {
    this.insertMenuTarget.classList.add("hidden")
    this.insertMenuOpen = false
    this.editor.chain().focus().toggleOrderedList().run()
    this.updateInlineInsert()
  }

  insertHeadingFromMenu() {
    this.insertMenuTarget.classList.add("hidden")
    this.insertMenuOpen = false
    this.editor.chain().focus().toggleHeading({ level: 2 }).run()
    this.updateInlineInsert()
  }

  updateInlineInsert() {
    if (!this.hasInlineInsertTarget) return
    const { view, state } = this.editor
    const container = this.contentTarget.getBoundingClientRect()

    if (!view.hasFocus()) {
      this.inlineInsertTarget.style.top = "0.5rem"
      this.inlineInsertTarget.style.left = "-0.25rem"
      this.inlineInsertTarget.classList.remove("hidden")
      this.inlineInsertTarget.classList.add("flex")
      return
    }
    if (this.slashOpen || this.insertMenuOpen || !state.selection.empty) {
      this.inlineInsertTarget.classList.add("hidden")
      return
    }
    const parent = state.selection.$from.parent
    const isParagraph = parent.type.name === "paragraph"
    const isEmptyParagraph = parent.content.size === 0
    const isAtStart = state.selection.$from.parentOffset === 0
    const isBlockedType = this.editor.isActive("heading") || this.editor.isActive("codeBlock") || this.editor.isActive("blockquote")
    if (!isParagraph || !isEmptyParagraph || !isAtStart || isBlockedType) {
      this.inlineInsertTarget.classList.add("hidden")
      this.insertMenuTarget.classList.add("hidden")
      this.insertMenuOpen = false
      return
    }

    const rect = view.coordsAtPos(state.selection.from)
    this.inlineInsertTarget.style.top = `${rect.top - container.top - 6}px`
    const baseLeft = -44
    const minLeft = 8 - container.left
    const adjustedLeft = Math.max(baseLeft, minLeft)
    this.inlineInsertTarget.style.left = `${adjustedLeft}px`
    this.inlineInsertTarget.classList.remove("hidden")
    this.inlineInsertTarget.classList.add("flex")
  }

  updateBubble() {
    if (!this.hasBubbleTarget) return
    const { state, view } = this.editor
    const isNodeMenu = this.editor.isActive("image") || this.editor.isActive("codeBlock")
    if (!isNodeMenu && (this.slashOpen || this.insertMenuOpen || state.selection.empty)) {
      this.bubbleTarget.classList.add("hidden")
      return
    }

    if (this.hasDeleteButtonTarget) {
      this.deleteButtonTarget.classList.toggle("hidden", !isNodeMenu)
    }
    if (this.hasFormatButtonsTarget) {
      this.formatButtonsTarget.classList.toggle("hidden", isNodeMenu)
    }

    const start = view.coordsAtPos(state.selection.from)
    const end = view.coordsAtPos(state.selection.to)
    const container = this.contentTarget.getBoundingClientRect()
    const bubbleRect = this.bubbleTarget.getBoundingClientRect()
    const center = (start.left + end.right) / 2
    const left = center - container.left - bubbleRect.width / 2
    const top = start.top - container.top - bubbleRect.height - 8

    this.bubbleTarget.style.left = `${Math.max(0, left)}px`
    this.bubbleTarget.style.top = `${Math.max(0, top)}px`
    this.bubbleTarget.classList.remove("hidden")
    this.bubbleTarget.classList.add("opacity-100", "translate-y-0")
  }

  deleteSelectedBlock() {
    if (this.editor.isActive("codeBlock")) {
      this.editor.chain().focus().deleteNode("codeBlock").run()
    } else if (this.editor.isActive("image")) {
      this.editor.chain().focus().deleteSelection().run()
    }
    this.updateInlineInsert()
    this.updateBubble()
  }

  handleEditorClick(view, pos, event) {
    const node = view.state.doc.nodeAt(pos)
    if (!node) return false
    if (node.type.name === "image" || node.type.name === "codeBlock") {
      this.editor.chain().focus().setNodeSelection(pos).run()
      this.updateBubble()
      return true
    }
    return false
  }

  promptLink(editor) {
    const url = window.prompt("Enter a URL")
    if (!url) return
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
  }

  uploadImage(event) {
    const files = Array.from(event.target.files || []).filter((f) => f.type.startsWith("image/"))
    if (!files.length) return
    const clearInput = () => {
      event.target.value = ""
    }
    const insertImage = (url) => this.editor.chain().focus().setImage({ src: url }).run()
    if (files.length === 1) {
      this.uploadImageFile(files[0]).then((url) => {
        insertImage(url)
        clearInput()
      }).catch(clearInput)
      return
    }
    Promise.all(files.map((file) => this.uploadImageFile(file)))
      .then((urls) => {
        urls.forEach((url) => insertImage(url))
        clearInput()
      })
      .catch(clearInput)
  }

  handleDrop(event) {
    const files = Array.from(event.dataTransfer?.files || []).filter((file) => file.type.startsWith("image/"))
    if (!files.length) return false
    event.preventDefault()
    files.forEach((file) => {
      this.uploadImageFile(file).then((url) => {
        this.editor.chain().focus().setImage({ src: url }).run()
      })
    })
    return true
  }

  handlePaste(event) {
    const files = Array.from(event.clipboardData?.files || []).filter((file) => file.type.startsWith("image/"))
    if (!files.length) return false
    event.preventDefault()
    files.forEach((file) => {
      this.uploadImageFile(file).then((url) => {
        this.editor.chain().focus().setImage({ src: url }).run()
      })
    })
    return true
  }

  uploadImageFile(file) {
    const token = document.querySelector("meta[name='csrf-token']").content
    const formData = new FormData()
    formData.append("file", file)

    return fetch("/uploads", {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": token
      },
      body: formData
    })
      .then((response) => response.json())
      .then((data) => data.url)
  }
}
